/**
 * AudioContext partagé + déblocage iOS.
 *
 * Safari sur iPhone démarre tout AudioContext en état "suspended" et ne le
 * réveille QUE dans le gestionnaire d'un vrai geste utilisateur. On appelle
 * donc `unlock()` au premier clic/tap, avant toute lecture.
 *
 * PIÈGE iOS #2 (son absent) : la sortie de la Web Audio API est coupée par
 * l'INTERRUPTEUR SILENCIEUX de l'iPhone, contrairement à un élément <audio>
 * (canal « média »). Pour que la lecture Web Audio sorte quand même du haut-
 * parleur, on garde en fond un petit <audio> silencieux qui joue en boucle :
 * cela bascule la session audio de la page en catégorie « playback », et la
 * Web Audio n'est alors plus muettée par le bouton silencieux.
 */

type AnyWindow = Window & {
  webkitAudioContext?: typeof AudioContext;
};

let ctx: AudioContext | null = null;
let silentEl: HTMLAudioElement | null = null;

export function getContext(): AudioContext {
  if (!ctx) {
    const w = window as AnyWindow;
    const Ctor = window.AudioContext ?? w.webkitAudioContext;
    if (!Ctor) throw new Error('Web Audio non disponible sur ce navigateur.');
    ctx = new Ctor();
  }
  return ctx;
}

/** Construit un data-URI WAV de ~0,1 s de silence (pas de gros blob dans le code). */
function silentWavUri(): string {
  const sr = 8000;
  const n = 800; // 0,1 s
  const bytes = 44 + n * 2;
  const v = new DataView(new ArrayBuffer(bytes));
  const w = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i));
  };
  w(0, 'RIFF'); v.setUint32(4, bytes - 8, true); w(8, 'WAVE'); w(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  w(36, 'data'); v.setUint32(40, n * 2, true);
  // les échantillons sont déjà à zéro (silence)
  const u8 = new Uint8Array(v.buffer);
  let bin = '';
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
  return 'data:audio/wav;base64,' + btoa(bin);
}

/** Démarre/relance le <audio> silencieux de fond (canal média → sort malgré le mode silencieux iOS). */
function primeMediaSession(): void {
  try {
    if (!silentEl) {
      const el = document.createElement('audio');
      el.setAttribute('playsinline', '');
      el.loop = true;
      el.preload = 'auto';
      el.src = silentWavUri();
      silentEl = el;
    }
    const pr = silentEl.play?.();
    if (pr && typeof pr.catch === 'function') pr.catch(() => {});
  } catch {
    /* pas bloquant : au pire, la Web Audio reste soumise au bouton silencieux */
  }
}

export async function unlock(): Promise<void> {
  const c = getContext();
  if (c.state === 'suspended') {
    try {
      await c.resume();
    } catch {
      /* ignoré : on retentera au prochain geste */
    }
  }
  // Un buffer muet suffit à « armer » la sortie sur iOS.
  const src = c.createBufferSource();
  src.buffer = c.createBuffer(1, 1, c.sampleRate);
  src.connect(c.destination);
  src.start(0);
  // Bascule la session en « playback » pour contourner l'interrupteur silencieux.
  primeMediaSession();
}

// Amorce le déblocage dès le tout premier geste, avant même la première lecture.
if (typeof document !== 'undefined') {
  const once = () => {
    unlock().catch(() => {});
    document.removeEventListener('pointerdown', once);
    document.removeEventListener('touchend', once);
    document.removeEventListener('keydown', once);
  };
  document.addEventListener('pointerdown', once, { capture: true });
  document.addEventListener('touchend', once, { capture: true });
  document.addEventListener('keydown', once, { capture: true });
}

/**
 * decodeAudioData compatible : Safari < 15 n'accepte que la forme à callbacks.
 */
export function decode(data: ArrayBuffer): Promise<AudioBuffer> {
  const c = getContext();
  return new Promise((resolve, reject) => {
    const maybePromise = c.decodeAudioData(
      data,
      (buf) => resolve(buf),
      (err) => reject(err ?? new Error('Décodage audio impossible.')),
    ) as unknown as Promise<AudioBuffer> | undefined;

    if (maybePromise && typeof maybePromise.then === 'function') {
      maybePromise.then(resolve).catch(reject);
    }
  });
}

export async function decodeBlob(blob: Blob): Promise<AudioBuffer> {
  const buf = await blobToArrayBuffer(blob);
  return decode(buf);
}

/** Blob.arrayBuffer() n'existe pas sur Safari 13 / anciens Android. */
export function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result as ArrayBuffer);
    fr.onerror = () => reject(fr.error ?? new Error('Lecture du fichier impossible.'));
    fr.readAsArrayBuffer(blob);
  });
}
